// PRD.282 — shared write path for the org-default Patient Workspace settings
// row. Extracted from PatientWorkspaceSettingsPage so both that page and Clinic
// Settings (clinic-type preset auto-apply / Apply Defaults) persist through the
// exact same atomic, grouped write (PRD §9 — reuse the existing write path).

import {
  mapUIToPatientWorkspaceSettingsColumns,
  type PatientWorkspaceSettingsUI,
} from '@helios/data-access';

/** Minimal structural view of the PowerSync db we depend on. */
interface ExecDb {
  execute(sql: string, params?: unknown[]): Promise<unknown>;
}

export interface PersistPatientWorkspaceArgs {
  orgId: string;
  /** The current org-default row, if one already exists (UPDATE vs INSERT). */
  existing?: { id: string } | null;
  values: Omit<PatientWorkspaceSettingsUI, 'id'>;
}

/**
 * Upsert the single org-default Patient Workspace settings row (clinic_id IS
 * NULL). All Sidebar + Visit Note tab columns are written together in one
 * `db.execute`, so a preset application never leaves one tab group half-updated.
 */
export async function persistPatientWorkspaceSettings(
  db: ExecDb,
  { orgId, existing, values }: PersistPatientWorkspaceArgs,
): Promise<void> {
  const cols = mapUIToPatientWorkspaceSettingsColumns(values);
  if (existing) {
    await db.execute(
      `UPDATE patient_workspace_settings SET
        workspace_mode = ?,
        show_treatment_record = ?,
        show_patient_timeline = ?,
        default_sidebar_state = ?,
        sidebar_show_overview = ?,
        sidebar_show_intake_form = ?,
        sidebar_show_vitals = ?,
        sidebar_show_allergies = ?,
        sidebar_show_visits = ?,
        sidebar_show_treatment_record = ?,
        sidebar_show_contact = ?,
        sidebar_show_pharmacy = ?,
        sidebar_show_packages = ?,
        sidebar_show_files_images = ?,
        sidebar_show_labs = ?,
        consult_show_summary = ?,
        consult_show_vitals = ?,
        consult_show_charting = ?,
        consult_show_notes = ?,
        consult_show_prescriptions = ?,
        consult_show_treatment_plan = ?,
        consult_show_forms = ?,
        consult_show_files = ?,
        consult_show_invoices = ?,
        consult_show_payments = ?,
        consult_show_chief_complaint = ?,
        consult_show_symptoms = ?,
        consult_show_diagnoses = ?,
        consult_show_pharmacy = ?,
        consult_show_labs = ?,
        consult_show_medical_history = ?,
        treatment_record_drawer_width = ?,
        patient_overview_display = ?,
        queue_action_pay_enabled = ?,
        queue_action_dispense_enabled = ?,
        updated_at = ?
      WHERE id = ?`,
      [
        cols.workspace_mode,
        cols.show_treatment_record,
        cols.show_patient_timeline,
        cols.default_sidebar_state,
        cols.sidebar_show_overview,
        cols.sidebar_show_intake_form,
        cols.sidebar_show_vitals,
        cols.sidebar_show_allergies,
        cols.sidebar_show_visits,
        cols.sidebar_show_treatment_record,
        cols.sidebar_show_contact,
        cols.sidebar_show_pharmacy,
        cols.sidebar_show_packages,
        cols.sidebar_show_files_images,
        cols.sidebar_show_labs,
        cols.consult_show_summary,
        cols.consult_show_vitals,
        cols.consult_show_charting,
        cols.consult_show_notes,
        cols.consult_show_prescriptions,
        cols.consult_show_treatment_plan,
        cols.consult_show_forms,
        cols.consult_show_files,
        cols.consult_show_invoices,
        cols.consult_show_payments,
        cols.consult_show_chief_complaint,
        cols.consult_show_symptoms,
        cols.consult_show_diagnoses,
        cols.consult_show_pharmacy,
        cols.consult_show_labs,
        cols.consult_show_medical_history,
        cols.treatment_record_drawer_width,
        cols.patient_overview_display,
        cols.queue_action_pay_enabled,
        cols.queue_action_dispense_enabled,
        cols.updated_at,
        existing.id,
      ],
    );
  } else {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    await db.execute(
      `INSERT INTO patient_workspace_settings
        (id, org_id, clinic_id, workspace_mode, show_treatment_record, show_patient_timeline,
         default_sidebar_state,
         sidebar_show_overview, sidebar_show_intake_form, sidebar_show_vitals,
         sidebar_show_allergies, sidebar_show_visits, sidebar_show_treatment_record,
         sidebar_show_contact, sidebar_show_pharmacy, sidebar_show_packages,
         sidebar_show_files_images, sidebar_show_labs,
         consult_show_summary, consult_show_vitals, consult_show_charting, consult_show_notes,
         consult_show_prescriptions, consult_show_treatment_plan, consult_show_forms,
         consult_show_files, consult_show_invoices, consult_show_payments,
         consult_show_chief_complaint, consult_show_symptoms, consult_show_diagnoses,
         consult_show_pharmacy, consult_show_labs, consult_show_medical_history,
         treatment_record_drawer_width,
         patient_overview_display,
         queue_action_pay_enabled, queue_action_dispense_enabled,
         created_at, updated_at)
       VALUES (?, ?, NULL, ?, ?, ?, ?,
               ?, ?, ?, ?, ?, ?, ?, ?, ?,
               ?, ?,
               ?, ?, ?, ?, ?, ?, ?, ?, ?, ?,
               ?, ?, ?, ?, ?, ?,
               ?,
               ?,
               ?, ?,
               ?, ?)`,
      [
        id,
        orgId,
        cols.workspace_mode,
        cols.show_treatment_record,
        cols.show_patient_timeline,
        cols.default_sidebar_state,
        cols.sidebar_show_overview,
        cols.sidebar_show_intake_form,
        cols.sidebar_show_vitals,
        cols.sidebar_show_allergies,
        cols.sidebar_show_visits,
        cols.sidebar_show_treatment_record,
        cols.sidebar_show_contact,
        cols.sidebar_show_pharmacy,
        cols.sidebar_show_packages,
        cols.sidebar_show_files_images,
        cols.sidebar_show_labs,
        cols.consult_show_summary,
        cols.consult_show_vitals,
        cols.consult_show_charting,
        cols.consult_show_notes,
        cols.consult_show_prescriptions,
        cols.consult_show_treatment_plan,
        cols.consult_show_forms,
        cols.consult_show_files,
        cols.consult_show_invoices,
        cols.consult_show_payments,
        cols.consult_show_chief_complaint,
        cols.consult_show_symptoms,
        cols.consult_show_diagnoses,
        cols.consult_show_pharmacy,
        cols.consult_show_labs,
        cols.consult_show_medical_history,
        cols.treatment_record_drawer_width,
        cols.patient_overview_display,
        cols.queue_action_pay_enabled,
        cols.queue_action_dispense_enabled,
        now,
        cols.updated_at,
      ],
    );
  }
}
